/**
 * .scr script runner (Faz 10.7 — OCS automation.rs parity, docs/
 * opencadstudio-parity/10-workflow-performance.md §3.5).
 *
 * OCS truth: automation is a headless JSON server and .scr exists only as
 * the `--script FILE` boot flag — "one command per line". yap writes the
 * runner: lines feed the shared dispatcher exactly like typed input
 * (executeDraftCommand), prompt values feed the active CommandInteract
 * session (point grammar through commitParsedPoint, numbers/text through
 * provideValue). Grammar:
 *
 *   - blank line  = Enter (completes a loop/interaction; no-op otherwise)
 *   - `; ...`     = comment line
 *   - `delay ms`  = optional pause (yap addition, OCS has no delay syntax)
 *   - anything else = one command line ("COMMAND arg1 arg2" or "x,y" values)
 *
 * Running state reports into the shared command-line log per line; Esc
 * stops the run; a failing line reports its line number and message and
 * halts the script (never a silent wrong result).
 */

import { executeDraftCommand, commitParsedPoint } from '../dynamicInput/dynamicInputEngine';
import { useCommandInteract } from '../commands/commandInteract';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';
import { isTauriHost } from '../../services/fileSystem/desktopIo';

export interface ScriptStep {
    /** 1-based source line for error reporting. */
    lineNo: number;
    kind: 'command' | 'enter' | 'delay' | 'comment';
    text: string;
}

interface RunState {
    running: boolean;
    abortRequested: boolean;
    /** Progress readout for the status pill / SCRIPTSTATUS-style queries. */
    lineNo: number;
    total: number;
}

const state: RunState = { running: false, abortRequested: false, lineNo: 0, total: 0 };

/** Parse script text into executable steps (blank = Enter, `;` = comment). */
export function parseScript(text: string): ScriptStep[] {
    return text.split(/\r?\n/).map((rawLine, i): ScriptStep => {
        const lineNo = i + 1;
        const line = rawLine.trim();
        if (!line) return { lineNo, kind: 'enter', text: '' };
        if (line.startsWith(';')) return { lineNo, kind: 'comment', text: line };
        const delay = line.match(/^delay\s+(\d+)$/i);
        if (delay) return { lineNo, kind: 'delay', text: delay[1] };
        return { lineNo, kind: 'command', text: line };
    });
}

function logLines(lines: string[]): void {
    useDynamicInputStore.getState().appendLog(lines);
}

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/** Run a parsed script to completion (or Esc/error). Returns the summary. */
export async function runScriptSteps(steps: ScriptStep[], sourceName: string): Promise<string> {
    if (state.running) return 'SCRIPT: a script is already running.';
    state.running = true;
    state.abortRequested = false;
    state.total = steps.length;
    state.lineNo = 0;

    const onEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') state.abortRequested = true;
    };
    window.addEventListener('keydown', onEsc, true);

    logLines([`SCRIPT: running "${sourceName}" — ${steps.length} line(s). (Esc stops)`]);

    let executed = 0;
    let summary = '';
    try {
        for (const step of steps) {
            state.lineNo = step.lineNo;
            if (state.abortRequested) {
                summary = `SCRIPT: stopped by Esc at line ${step.lineNo}.`;
                break;
            }
            try {
                if (step.kind === 'comment') continue;
                if (step.kind === 'delay') {
                    await sleep(Math.min(10_000, Number(step.text) || 0));
                    continue;
                }
                if (step.kind === 'enter') {
                    const interact = useCommandInteract.getState();
                    if (interact.active && interact.spec) {
                        interact.finishActive();
                        logLines([`SCRIPT ${step.lineNo}: <Enter>`, '']);
                    }
                    continue;
                }
                await runScriptLine(step);
                executed++;
                // Yield between lines so the renderer paints and Esc lands.
                await sleep(0);
            } catch (e) {
                summary = `SCRIPT error at line ${step.lineNo}: ${e instanceof Error ? e.message : String(e)}`;
                break;
            }
        }
    } finally {
        window.removeEventListener('keydown', onEsc, true);
        state.running = false;
        state.lineNo = 0;
        state.total = 0;
    }

    if (!summary) {
        summary = state.abortRequested
            ? `SCRIPT: stopped by Esc after ${executed} line(s).`
            : `SCRIPT: "${sourceName}" finished — ${executed} line(s) executed.`;
    }
    logLines([summary, '']);
    return summary;
}

/** One command line: prompt values go to the active interaction, everything
 *  else dispatches through the shared chain (same as typed input). */
async function runScriptLine(step: ScriptStep): Promise<void> {
    const interact = useCommandInteract.getState();
    if (interact.active && interact.spec) {
        const act = interact.spec.steps[interact.stepIndex];
        if (!act) {
            // Engine between steps: treat as Enter (advances/completes).
            interact.finishActive();
            return;
        }
        if (act.kind === 'point') {
            const outcome = commitParsedPoint({ input: step.text });
            logLines([`SCRIPT ${step.lineNo}: ${step.text}`, outcome.line, '']);
            if (!outcome.ok) throw new Error(outcome.line);
            return;
        }
        const numeric = Number(step.text);
        const value = act.kind === 'numeric' && Number.isFinite(numeric) ? numeric : step.text;
        interact.provideValue(value);
        logLines([`SCRIPT ${step.lineNo}: ${step.text}`, '']);
        return;
    }
    const outcome = executeDraftCommand(step.text);
    logLines(outcome.lines);
    if (!outcome.success) throw new Error(outcome.message || 'command failed');
}

/** True while a script feeds the command stream. */
export function isScriptRunning(): boolean {
    return state.running;
}

/** Progress snapshot (line being executed / total lines). */
export function scriptProgress(): { lineNo: number; total: number } {
    return { lineNo: state.lineNo, total: state.total };
}

/** Request a stop from UI surfaces (the Esc listener covers the keyboard). */
export function stopScript(): void {
    if (state.running) state.abortRequested = true;
}

/** Load script text from a path/URL: fetch first (relative URLs, http[s]),
 *  then the Tauri shell's fsio_read_text for absolute desktop paths. */
export async function loadScriptSource(source: string): Promise<string> {
    const looksLikeUrl = /^(https?:)?\/\//i.test(source) || !/^([a-zA-Z]:[\\/]|\\\\)/.test(source);
    if (looksLikeUrl) {
        const res = await fetch(source);
        if (!res.ok) throw new Error(`fetch failed (${res.status})`);
        return res.text();
    }
    if (isTauriHost()) {
        const core = (window as unknown as { __TAURI__?: { core?: { invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown> } } }).__TAURI__?.core;
        const text = await core?.invoke?.('fsio_read_text', { path: source }) as string | null;
        if (text === null || text === undefined) throw new Error(`file not found: ${source}`);
        return text;
    }
    throw new Error(`cannot read "${source}" outside the desktop shell`);
}

/**
 * Boot flag (OCS `--script FILE` parity): `?script=<path-or-url>` in the
 * app URL runs one .scr after the 2D workspace mounts. Failures report to
 * the command log — a broken boot script never blocks the app.
 */
export async function maybeRunBootScript(): Promise<boolean> {
    const source = new URLSearchParams(window.location.search).get('script');
    if (!source) return false;
    try {
        const text = await loadScriptSource(source);
        const name = source.split(/[\\/]/).pop() || 'boot.scr';
        await runScriptSteps(parseScript(text), name);
    } catch (e) {
        logLines([`SCRIPT boot failed for "${source}": ${e instanceof Error ? e.message : String(e)}`, '']);
    }
    return true;
}
